<div class="role-detail-page">

  @if (isLoading) {
    <div class="loading-state">
      <span class="loading-spinner"></span>
      <span>Loading role…</span>
    </div>
  }

  @if (!isLoading && role) {

    <!-- ── Page header ───────────────────────────── -->
    <app-page-header
      section="Administration / Roles & Permissions"
      [pageTitle]="role.name"
      [badgeText]="displayCode"
      [subtitle]="headerSubtitle"
      [actions]="pageHeaderActions">
    </app-page-header>

    <!-- ── Role card ─────────────────────────────── -->
    <div class="rp-card">

      <!-- Card header: role identity + actions -->
      <div class="rp-card__header">
        <div class="rp-card__role-info">
          <div class="rp-card__role-text">
            <h2 class="rp-card__role-name">{{ role.name }}</h2>
            @if (role.description) {
              <p class="rp-card__role-desc">{{ role.description }}</p>
            }
          </div>
        </div>
        <div class="rp-card__actions">
          <button *appHasPermission="'roles.create'" type="button" class="btn-ghost" (click)="duplicate()">Duplicate</button>
          <button *appHasPermission="'roles.update'" type="button" class="btn-ghost" (click)="editRole()">Edit</button>
          <button
            *appHasPermission="'roles.delete'"
            type="button"
            class="btn-danger"
            [disabled]="role.usersCount > 0"
            (click)="openDeleteModal()">
            Delete role
          </button>
        </div>
      </div>

      <!-- Stats row -->
      <div class="rp-stats">
        <div class="rp-stat">
          <span class="rp-stat__label">ASSIGNED USERS</span>
          <span class="rp-stat__value rp-stat__value--teal">{{ role.usersCount }}</span>
        </div>
        <div class="rp-stat-divider"></div>
        <div class="rp-stat">
          <span class="rp-stat__label">PERMISSIONS</span>
          <span class="rp-stat__value">
            {{ totalGranted }}<span class="rp-stat__total">/{{ totalPermissions }}</span>
          </span>
        </div>
        <div class="rp-stat-divider"></div>
        <div class="rp-stat">
          <span class="rp-stat__label">CREATED</span>
          <span class="rp-stat__value rp-stat__value--muted">
            {{ role.createdAt ? (role.createdAt | date:'d MMM yyyy') : '—' }}
          </span>
        </div>
      </div>

      <!-- Warning: users assigned, cannot delete -->
      @if (role.usersCount > 0) {
        <div class="rp-banner rp-banner--red">
          <svg class="rp-banner__icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2"/>
          </svg>
          <div class="rp-banner__body">
            <span class="rp-banner__strong">Cannot delete — {{ role.usersCount }} active user{{ role.usersCount !== 1 ? 's' : '' }} assigned.</span>
            Deleting this role would revoke access for all of them. Reassign these users to another role first.
            <div class="rp-banner__actions">
              <button type="button" class="rp-banner__btn" (click)="viewAffectedUsers()">View affected users</button>
            </div>
          </div>
        </div>
      }

      <!-- Modules accordion -->
      <div class="rp-modules">
        @for (mod of panelModules; track mod.name) {
          <div class="rp-module">
            <div class="rp-module__row" (click)="toggleModule(mod.name)">
              <button type="button" class="rp-module__chevron" [class.rp-module__chevron--open]="openModules[mod.name]" aria-label="Toggle module">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                  <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708"/>
                </svg>
              </button>
              <img class="rp-module__icon" [src]="mod.icon" [alt]="mod.name" />
              <span class="rp-module__name">{{ mod.name }}</span>
              <div class="rp-module__progress-wrap">
                <div class="rp-module__track">
                  <div class="rp-module__fill" [style.width.%]="getProgressPercent(mod)"></div>
                </div>
                <span class="rp-module__count" [class.rp-module__count--active]="mod.granted > 0">
                  <span class="rp-module__count-granted">{{ mod.granted }}</span><span class="rp-module__count-sep">/{{ mod.total }}</span>
                </span>
              </div>
            </div>

            @if (openModules[mod.name]) {
              <div class="rp-perms">
                @for (perm of mod.permissions; track perm.code) {
                  <div class="rp-perm" [class.rp-perm--granted]="perm.granted">
                    <span class="rp-perm__icon">
                      @if (perm.granted) {
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                        </svg>
                      } @else {
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                        </svg>
                      }
                    </span>
                    <span class="rp-perm__name">{{ perm.name }}</span>
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>

    </div><!-- /rp-card -->

  }

</div>

<!-- Delete confirmation modal -->
@if (deleteModalData) {
  <app-confirm-action-modal
    [data]="deleteModalData"
    [isOpen]="isDeleteModalOpen"
    (actionTriggered)="onDeleteModalAction($event)"
    (dismissed)="closeDeleteModal()">
  </app-confirm-action-modal>
}
